import { DefaultFilterEnum, FontLocation } from 'shared/consts';
import { ThemeLocationFont, useThemeFont } from 'hooks/use-theme-font.hook';
import { selectAppInstanceConfigs } from 'store/app-instance-configs/selectors';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AppInstanceConfig } from 'store/app-instance-configs/types';
import { selectTransactions } from 'store/transactions/selectors';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';
import { useLoadingHandler } from './loading-handler.hook';
import { useAppInstanceConfigFeature } from 'hooks/use-app-instance-config-feature.hook';

interface UseLandingPageReturnValues {
  shouldShowSkeleton: boolean;
  isMobileDevice: boolean;
  showAvailableNow: boolean;
  showWhenIsBooking: boolean;
  showResourcesList: boolean;
  showFloorPlan: boolean;
  isCompactUiEnabled: boolean;
  themeFontTitle: ThemeLocationFont;
  appInstanceConfigs: Array<AppInstanceConfig>;
  defaultFilter: DefaultFilterEnum;
}

export const useLandingPageData = (): UseLandingPageReturnValues => {
  const isMobileDevice = useIsSmallViewportWidth();
  const themeFontTitle = useThemeFont(FontLocation.TITLE);
  const appInstanceConfigs = useSelector(selectAppInstanceConfigs);
  const transactions = useSelector(selectTransactions);
  const { hideResourceBookingAvailableNow: isAvailableNowFeatureHidden, showFloorPlan } = useFlags();
  const isDataLoading = useLoadingHandler();
  const { defaultFilter, isAvailableNowEnabled, isNativeAdapter, hideWhenIsBooking, isCompactUiEnabled } =
    useAppInstanceConfigFeature();

  const shouldShowSkeleton = isDataLoading || !transactions;
  const showResourcesList = defaultFilter === DefaultFilterEnum.NONE;

  const showAvailableNow = useMemo(() => {
    return isAvailableNowEnabled && !showResourcesList && !isAvailableNowFeatureHidden;
  }, [isAvailableNowEnabled, isAvailableNowFeatureHidden, showResourcesList]);

  const showWhenIsBooking = useMemo(() => {
    if (isNativeAdapter) {
      return !hideWhenIsBooking;
    }
    return true;
  }, [isNativeAdapter, hideWhenIsBooking]);

  return {
    isCompactUiEnabled,
    defaultFilter,
    showResourcesList,
    showAvailableNow,
    showWhenIsBooking,
    showFloorPlan,
    shouldShowSkeleton,
    isMobileDevice,
    themeFontTitle,
    appInstanceConfigs,
  };
};
